import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons/faTriangleExclamation';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { TAB_EXCLUSION_REASONS, TAB_STATUSES } from '../helpers/upload.types';
import type { TabAnalysis, TabExclusionReason } from '../helpers/upload.types';
import { IssuesTable } from './issues-table';
import styles from './tab-result-card.module.scss';

const STATUS_ICONS = {
  [TAB_STATUSES.CLEAN]: faCircleCheck,
  [TAB_STATUSES.PARTIAL]: faTriangleExclamation,
  [TAB_STATUSES.EXCLUDED]: faCircleExclamation,
};

const STATUS_STYLES = {
  [TAB_STATUSES.CLEAN]: styles.clean,
  [TAB_STATUSES.PARTIAL]: styles.partial,
  [TAB_STATUSES.EXCLUDED]: styles.excluded,
};

const EXCLUSION_LANG_KEYS: Record<TabExclusionReason, string> = {
  [TAB_EXCLUSION_REASONS.MISSING_SHIFT_TITLE]: 'SHIFTS_REASON_MISSING_SHIFT_TITLE',
  [TAB_EXCLUSION_REASONS.NO_DATE_COLUMNS]: 'SHIFTS_REASON_NO_DATE_COLUMNS',
  [TAB_EXCLUSION_REASONS.NO_SHIFTS]: 'SHIFTS_REASON_NO_SHIFTS',
  [TAB_EXCLUSION_REASONS.NOTHING_TO_IMPORT]: 'SHIFTS_REASON_NOTHING_TO_IMPORT',
};

type TabResultCardProps = {
  tab: TabAnalysis;
};

export const TabResultCard = ({ tab }: TabResultCardProps) => {
  const lang = useSelector(getLang('SETTINGS'));
  const [showIssues, setShowIssues] = useState(false);

  const hasIssues = tab.issues.length > 0;

  const summary =
    tab.status === TAB_STATUSES.EXCLUDED
      ? lang[EXCLUSION_LANG_KEYS[tab.exclusionReason]]
      : (tab.status === TAB_STATUSES.PARTIAL ? lang.SHIFTS_TAB_PARTIAL : lang.SHIFTS_TAB_CLEAN)
          .replace('{count}', formatNumber(tab.importedCount))
          .replace('{skipped}', formatNumber(tab.skippedCount));

  return (
    <div className={classNames(styles.root, STATUS_STYLES[tab.status])}>
      <div className={styles.row}>
        <span className={styles.iconChip}>
          <FontAwesomeIcon icon={STATUS_ICONS[tab.status]} />
        </span>
        <div className={styles.body}>
          <span className={styles.summary}>
            <span className={styles.sheetName}>{tab.sheetName}</span> — {summary}
          </span>
          {tab.status === TAB_STATUSES.EXCLUDED && (
            <span className={styles.subLine}>{lang.SHIFTS_TAB_EXCLUDED_NOTE}</span>
          )}
        </div>
        {hasIssues && (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={showIssues}
            onClick={() => setShowIssues(current => !current)}>
            {showIssues
              ? lang.SHIFTS_HIDE_ISSUES
              : lang.SHIFTS_VIEW_ISSUES.replace('{count}', formatNumber(tab.issues.length))}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={classNames(styles.chevron, showIssues && styles.chevronOpen)}
            />
          </button>
        )}
      </div>
      {hasIssues && showIssues && <IssuesTable issues={tab.issues} />}
    </div>
  );
};
